/**
 * <bk-collection-tabs> turns the server-rendered row of collection links into an ARIA tablist and
 * swaps the product panel through the Section Rendering API.
 *
 * The HTML ships as plain links plus one server-rendered panel, so the section works with no
 * JavaScript at all. On connect this module adds the tab roles, a roving tabindex and keyboard
 * support. Activation is manual (arrows move focus; Enter, Space or a click opens a tab), as the
 * WAI-ARIA tabs pattern recommends when a panel needs a network request.
 * Opening a tab fetches `<collection url>?section_id=collection-tabs`: requested by file name on a
 * collection URL, the section answers with the bare panel of that collection. The panel keeps as
 * many cards as this instance shows (data-limit), its ids are prefixed with this section's id so
 * two instances never share one, and it is cached per tab for the life of the page.
 * Switching has to feel immediate, so the panels are fetched before they are asked for: every
 * category is warmed once the page is quiet (one at a time, never on a 2G or data-saver
 * connection), and a finger or a mouse landing on a tab warms that one first. A tab that still has
 * to wait shows empty cards after a moment rather than the previous category standing there.
 *
 * No string is built here: each tab carries its translated, pluralised labels in data-view-all and
 * data-announce, and the tablist its name in data-label.
 */
import { withTimeout } from '@theme/utils';
const BARE_SECTION_ID = 'collection-tabs';
const ID_REFERENCES = ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for', 'form', 'list'];
/** Below this, the panel swaps straight from one category to the next and no skeleton is shown. */
const SKELETON_AFTER = 120;
/** How long the page waits before it warms the other categories in the background. */
const WARM_AFTER = 1200;

class BkCollectionTabs extends HTMLElement {
  /** @type {HTMLElement[]} */
  #tabs = [];
  /** @type {HTMLElement | null} */
  #panel = null;
  /** @type {HTMLElement | null} */
  #status = null;
  /** @type {HTMLAnchorElement | null} */
  #viewAll = null;
  /** @type {HTMLElement | null} */
  #viewAllLabel = null;
  /** @type {HTMLElement | null} */
  #selected = null;
  /** Panels, each one a promise so a tab tapped twice, or warmed then tapped, fetches once.
   * @type {Map<string, Promise<string>>} */
  #cache = new Map();
  /** @type {HTMLTemplateElement | null} */
  #skeleton = null;
  /** @type {number} */
  #run = 0;

  connectedCallback() {
    const tablist = this.querySelector('[data-tablist]');
    this.#panel = this.querySelector('[data-tab-panel]');
    this.#status = this.querySelector('[data-tabs-status]');
    this.#viewAll = this.querySelector('[data-tabs-view-all]');
    this.#viewAllLabel = this.querySelector('[data-tabs-view-all-label]');
    this.#tabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-tab]')]);
    if (!(tablist instanceof HTMLElement) || !this.#panel || this.#tabs.length < 2) return;

    const panelUrl = this.#panel.getAttribute('data-url');
    if (panelUrl) this.#cache.set(panelUrl, Promise.resolve(this.#panel.innerHTML));
    const skeleton = this.querySelector('[data-tabs-skeleton]');
    if (skeleton instanceof HTMLTemplateElement) this.#skeleton = skeleton;

    tablist.setAttribute('role', 'tablist');
    const labelledBy = tablist.getAttribute('data-labelledby');
    if (labelledBy && document.getElementById(labelledBy)) {
      tablist.setAttribute('aria-labelledby', labelledBy);
    } else {
      tablist.setAttribute('aria-label', tablist.getAttribute('data-label') ?? '');
    }
    this.#panel.setAttribute('role', 'tabpanel');

    for (const tab of this.#tabs) {
      tab.setAttribute('role', 'tab');
      tab.removeAttribute('aria-current');
      tab.setAttribute('aria-controls', this.#panel.id);
      tab.addEventListener('click', this.#onClick);
      tab.addEventListener('keydown', this.#onKeydown);
      // A finger resting on a tab, or a mouse passing over it, starts the panel before the tap lands.
      tab.addEventListener('pointerenter', this.#onWarm);
      tab.addEventListener('pointerdown', this.#onWarm);
      tab.addEventListener('focus', this.#onWarm);
    }

    const current = this.#tabs.find((tab) => tab.getAttribute('data-url') === panelUrl) ?? this.#tabs[0];
    this.#markSelected(current);
    this.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.#warmAll();
  }

  disconnectedCallback() {
    this.#run += 1;
    for (const tab of this.#tabs) {
      tab.removeEventListener('click', this.#onClick);
      tab.removeEventListener('keydown', this.#onKeydown);
      tab.removeEventListener('pointerenter', this.#onWarm);
      tab.removeEventListener('pointerdown', this.#onWarm);
      tab.removeEventListener('focus', this.#onWarm);
    }
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  #onClick = (/** @type {MouseEvent} */ event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;
    event.preventDefault();
    this.#activate(tab);
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;
    const index = this.#tabs.indexOf(tab);
    if (index < 0) return;

    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.#activate(tab);
      return;
    }

    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = -1;
    if (event.key === 'ArrowRight') next = rtl ? index - 1 : index + 1;
    else if (event.key === 'ArrowLeft') next = rtl ? index + 1 : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.#tabs.length - 1;
    else return;

    event.preventDefault();
    this.#tabs[(next + this.#tabs.length) % this.#tabs.length]?.focus();
  };

  /** Theme editor: selecting a tab block opens that tab. */
  #onBlockSelect = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const tab = target.matches('[data-tab]') ? target : target.querySelector('[data-tab]');
    if (tab instanceof HTMLElement && this.#tabs.includes(tab)) this.#activate(tab);
  };

  /** @param {HTMLElement} tab */
  #markSelected(tab) {
    this.#selected = tab;
    for (const other of this.#tabs) {
      const isSelected = other === tab;
      other.setAttribute('aria-selected', String(isSelected));
      other.setAttribute('tabindex', isSelected ? '0' : '-1');
    }
    this.#panel?.setAttribute('aria-labelledby', tab.id);

    const url = tab.getAttribute('data-url');
    if (this.#viewAll && url) this.#viewAll.setAttribute('href', url);
    const viewAllLabel = tab.getAttribute('data-view-all');
    if (this.#viewAllLabel && viewAllLabel) this.#viewAllLabel.textContent = viewAllLabel;
  }

  /** Start a panel without opening it, and keep the one promise for whoever needs it.
   * @param {string} url @returns {Promise<string>} */
  #load(url) {
    const pending = this.#cache.get(url);
    if (pending) return pending;
    // A failed panel must not poison the cache: drop it so the next tap tries again.
    const fresh = this.#fetchPanel(url).catch((error) => {
      this.#cache.delete(url);
      throw error;
    });
    this.#cache.set(url, fresh);
    return fresh;
  }

  #onWarm = (/** @type {Event} */ event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;
    const url = tab.getAttribute('data-url');
    if (url) this.#load(url).catch(() => {});
  };

  /** Warm every other category once the page is quiet, so switching is instant from then on. */
  #warmAll() {
    const connection = /** @type {{ saveData?: boolean, effectiveType?: string }} */ (
      /** @type {any} */ (navigator).connection ?? {}
    );
    if (connection.saveData || /2g/.test(connection.effectiveType ?? '')) return;

    const run = this.#run;
    const urls = this.#tabs.map((tab) => tab.getAttribute('data-url')).filter(Boolean);
    const next = async () => {
      for (const url of urls) {
        if (run !== this.#run || !this.isConnected) return;
        // One at a time: the visitor's own taps and images come first.
        await this.#load(/** @type {string} */ (url)).catch(() => {});
      }
    };
    const idle = /** @type {any} */ (window).requestIdleCallback;
    if (typeof idle === 'function') idle(() => next(), { timeout: 4000 });
    else setTimeout(next, WARM_AFTER);
  }

  /** @param {HTMLElement} tab */
  async #activate(tab) {
    const panel = this.#panel;
    const url = tab.getAttribute('data-url');
    if (!panel || !url || tab === this.#selected) return;
    this.#markSelected(tab);

    this.#run += 1;
    const run = this.#run;
    const pending = this.#load(url);
    panel.setAttribute('aria-busy', 'true');

    // Empty cards, but only if the category makes the visitor wait: a warm one swaps with no flash.
    const waiting = setTimeout(() => {
      if (run === this.#run) this.#showSkeleton();
    }, SKELETON_AFTER);

    /** @type {string} */
    let html;
    try {
      html = await pending;
    } catch (error) {
      clearTimeout(waiting);
      if (run !== this.#run) return;
      // The tab is a link to its collection: follow it rather than leave empty cards on screen.
      window.location.assign(url);
      return;
    }
    clearTimeout(waiting);
    if (run !== this.#run || this.#selected !== tab) return;
    panel.removeAttribute('aria-busy');
    panel.innerHTML = html;
    panel.setAttribute('data-url', url);

    const announcement = tab.getAttribute('data-announce');
    if (this.#status && announcement) {
      this.#status.textContent = '';
      requestAnimationFrame(() => {
        if (this.#status) this.#status.textContent = announcement;
      });
    }
  }

  /** Empty cards in place of the products, as many as this instance shows. */
  #showSkeleton() {
    const panel = this.#panel;
    const template = this.#skeleton;
    if (!panel || !template) return;
    panel.replaceChildren(template.content.cloneNode(true));
  }

  /**
   * @param {string} url
   * @returns {Promise<string>}
   */
  async #fetchPanel(url) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', BARE_SECTION_ID);

    const response = await fetch(target, { signal: withTimeout(undefined) });
    if (!response.ok) throw new Error(`Section Rendering API: HTTP ${response.status}`);

    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const bare = parsed.querySelector('[data-collection-tabs-bare]');
    if (!(bare instanceof HTMLElement)) throw new Error('Section Rendering API: no panel in the response');

    this.#trim(bare);
    this.#prefixIds(bare);
    return bare.innerHTML;
  }

  /** Keep only as many cards as this instance shows. @param {HTMLElement} root */
  #trim(root) {
    const limit = Number.parseInt(this.getAttribute('data-limit') ?? '', 10);
    if (!Number.isFinite(limit) || limit < 1) return;
    const items = [...root.querySelectorAll('.collection-tabs__item')];
    for (const item of items.slice(limit)) item.remove();
  }

  /** Prefix every id (and every reference to it) with this section's id. @param {HTMLElement} root */
  #prefixIds(root) {
    const prefix = this.getAttribute('data-section-id');
    if (!prefix) return;

    // Attributes, not the `id` property: a product form holding <input name="id"> clobbers form.id.
    /** @type {Map<string, string>} */
    const renamed = new Map();
    for (const element of root.querySelectorAll('[id]')) {
      const current = element.getAttribute('id') ?? '';
      if (!current) continue;
      const fresh = `${prefix}-${current}`;
      renamed.set(current, fresh);
      element.setAttribute('id', fresh);
    }
    if (renamed.size === 0) return;

    for (const attribute of ID_REFERENCES) {
      for (const element of root.querySelectorAll(`[${attribute}]`)) {
        const value = element.getAttribute(attribute) ?? '';
        const mapped = value
          .split(/\s+/)
          .filter(Boolean)
          .map((id) => renamed.get(id) ?? id)
          .join(' ');
        element.setAttribute(attribute, mapped);
      }
    }
  }
}

if (!customElements.get('bk-collection-tabs')) customElements.define('bk-collection-tabs', BkCollectionTabs);
